import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import * as SearchActions from '~app/actions/search';
import createSingleTileImageLayer from '~app/core/createSingleTileImageLayer';
import createTiledImageLayer from '~app/core/createTiledImageLayer';
import defined from '~app/core/defined';
import Logging from '~app/core/logging';

import CesiumGlobe from './CesiumGlobe';

const log = Logging.getLogger('app.components.Globe');

const selectSkyboxImages = [
  require('~app/resources/images/sky_box/tycho2t3_80_px.jpg'),
  require('~app/resources/images/sky_box/tycho2t3_80_mx.jpg'),
  require('~app/resources/images/sky_box/tycho2t3_80_py.jpg'),
  require('~app/resources/images/sky_box/tycho2t3_80_my.jpg'),
  require('~app/resources/images/sky_box/tycho2t3_80_pz.jpg'),
  require('~app/resources/images/sky_box/tycho2t3_80_mz.jpg'),
];
const compareSkyboxImages = [
  require('~app/resources/images/sky_box/tycho2t3_80_grey_px.jpg'),
  require('~app/resources/images/sky_box/tycho2t3_80_grey_mx.jpg'),
  require('~app/resources/images/sky_box/tycho2t3_80_grey_py.jpg'),
  require('~app/resources/images/sky_box/tycho2t3_80_grey_my.jpg'),
  require('~app/resources/images/sky_box/tycho2t3_80_grey_pz.jpg'),
  require('~app/resources/images/sky_box/tycho2t3_80_grey_mz.jpg'),
];

class Globe extends Component {
  static propTypes = {
    mode: PropTypes.string.isRequired,
    morphResolver: PropTypes.func,
    isBusy: PropTypes.bool,
    mapDefs: PropTypes.array,
    vectors: PropTypes.array,
    labels: PropTypes.array,
    activeMapIndex: PropTypes.number,
  };

  constructor(props) {
    super(props);

    // Set state directly in the constructor to make sure that the first set
    // of props is applied correctly.
    const lowResMaps = this.unpackLowResLayers(props.lowResLayers, props.activeLayerIndex);
    const maps = this.generateMaps(props.mapDefs, props.allowHighResMaps,
                                   props.useHighResMaps, props.activeMapIndex);
    this.state = {
      isMorphing: false,
      isFlying: false,

      lowResMaps,
      maps,
      allMaps: [...lowResMaps, ...maps],
      activeMaps: [
        lowResMaps[this.getActiveLowResMapIndex(props)],
        maps[this.getActiveHighResMapIndex(props)],
      ],
    };

    this.mapsToDestroy = [];
  }

  onTap(location) {
    log.trace(`received tap with location=${location}`);

    this.cancelFlight();

    this.props.clearSearchTerm();

    if (defined(location)) {
      this.props.executeTapQuery(location);
    }
    else if (this.props.selectionMode === SearchActions.SelectionMode.select) {
      // Only clear the selection on an outside tap if we are in select mode.
      // In compare mode, we want to keep the original selection intact so that
      // our second pick can compare against it.
      this.props.clearSelection();
      this.props.clearPins();
    }
  }

  onSelect(object, location) {
    if (this.props.pinMarkers.includes(object)) {
      // Select a new pinned location from the current set of search locations.
      log.trace(`received select with pin=${object}`);
      this.props.selectPin(object);
    }
    else if (this.props.favoriteMarkers.includes(object)) {
      // Select a new favorite location, which dismisses the current search
      // locations (in selection mode).
      log.trace(`received select with star=${object}`);

      if (this.props.selectionMode === SearchActions.SelectionMode.select) {
        this.props.clearSearchTerm();
        this.props.clearPins();
      }

      this.props.selectFavorite(object);
    }
    else {
      this.onTap(location);
    }
  }

  componentDidMount() {
    this.globe.mode = this.props.mode;

    const lowResMaps = this.unpackLowResLayers(this.props.lowResLayers, this.props.activeLayerIndex);
    const maps = this.generateMaps(
      this.props.mapDefs, this.props.allowHighResMaps,
      this.props.useHighResMaps, this.props.activeMapIndex);

    this.setState({
      lowResMaps,
      maps,
      allMaps: [...lowResMaps, ...maps],
      activeMaps: [
        lowResMaps[this.getActiveLowResMapIndex(this.props)],
        maps[this.getActiveHighResMapIndex(this.props)],
      ],
    });
  }

  componentWillReceiveProps(nextProps) {
    this.handleMorphProps(nextProps);
    this.handleFlyToProps(nextProps);
    this.handleLayerProps(nextProps);
  }

  componentDidUpdate() {
    for (const map of this.mapsToDestroy) {
      map.destroy();
    }
    this.mapsToDestroy = [];
  }

  handleMorphProps(nextProps) {
    const {morphResolver} = nextProps;
    if (!!morphResolver && !this.state.isMorphing) {
      let morph;
      if (this.globe.mode === '3D') {
        morph = this.globe.morph('2D');
      }
      else {
        morph = this.globe.morph('3D');
      }
      morph.then(mode => {
        morphResolver(mode);
        this.setState(prevState => ({...prevState, isMorphing: false}));
      });
      this.setState({isMorphing: true});
    }
    // forward an explicit mode change to the globe
    else if (nextProps.mode !== this.props.mode) {
      this.globe.mode = nextProps.mode;
    }
  }

  handleFlyToProps(nextProps) {
    const {flyToResolver} = nextProps;
    if (!!flyToResolver && !this.state.isFlying) {
      this.globe.flyTo(nextProps.flyToLocation, nextProps.flyToDuration)
        .then((flightComplete) => {
          flyToResolver(flightComplete);
          this.setState(prevState => ({...prevState, isFlying: false}));
        });
      this.setState({isFlying: true});
    }
  }

  cancelFlight() {
    this.globe.cancelFlight();
    if (this.props.flyToResolver) {
      this.props.flyToResolver(false);
    }
  }

  handleLayerProps(nextProps) {
    // First, unpack any new low res maps into a flattened structure.
    // We don't want to ever do this redundantly so that we can do a shallow
    // comparison in the CesiumGlobe to determine if an update is required.
    // We want to avoid updates because they cause flickers.
    const nextState = {};

    let mapsChanged = false;
    let lowResMaps = this.state.lowResMaps;
    if (this.props.lowResLayers !== nextProps.lowResLayers ||
        this.props.activeLayerIndex !== nextProps.activeLayerIndex) {
      lowResMaps = this.unpackLowResLayers(nextProps.lowResLayers, nextProps.activeLayerIndex);
      nextState.lowResMaps = lowResMaps;
      mapsChanged = true;
    }

    let maps = this.state.maps;
    if (this.props.mapDefs !== nextProps.mapDefs ||
        this.props.allowHighResMaps !== nextProps.allowHighResMaps ||
        (nextProps.allowHighResMaps && nextProps.useHighResMaps &&
         this.props.activeMapIndex !== nextProps.activeMapIndex)) {
      // Defer destruction of the old layer's maps until they have been removed
      // from the Cesium globe.
      this.mapsToDestroy = [...this.state.maps];

      maps = this.generateMaps(
        nextProps.mapDefs, nextProps.allowHighResMaps,
        nextProps.useHighResMaps, nextProps.activeMapIndex);
      nextState.maps = maps;
      mapsChanged = true;
    }

    if (mapsChanged) {
      nextState.allMaps = [...lowResMaps, ...maps];
    }

    if (mapsChanged || this.props.activeMapIndex !== nextProps.activeMapIndex) {

      nextState.activeMaps = [
        lowResMaps[this.getActiveLowResMapIndex(nextProps)],
        maps[this.getActiveHighResMapIndex(nextProps)],
      ];

      this.setState(nextState);
    }
  }

  unpackLowResLayers(layers, activeLayerIndex) {
    // Take the layer definitions for the current layer, and the prev and next layers.
    const currentLayer = layers[activeLayerIndex];

    // We require all low-res images for the current player, plus the first
    // frame of all other layers. This allows the user to spam through all
    // layers quickly without ever seeing a blank globe.
    return layers.reduce((lowResLayers, layer) => {
      if (layer !== currentLayer) {
        lowResLayers.push(layer[0]);
      }
      return lowResLayers;
    }, [...currentLayer]);
  }

  getActiveLowResMapIndex({activeLayerIndex, activeMapIndex, lowResLayers}) {
    // We used to have a more complex computation here.
    // But now we have fewer low-res maps generated from unpackLowResLayers,
    // and the active layer's low-res maps are at the beginning of the array,
    // so this computation is now easy.
    return activeMapIndex;
  }

  getActiveHighResMapIndex({allowHighResMaps, useHighResMaps, activeMapIndex}) {
    // If we are rendering a high-res layer, then we only load one high-res
    // frame at a time, so we always use index 0. Otherwise, we can have
    // all of the med-res frames loaded, so we use the activeMapIndex there.
    return (allowHighResMaps && useHighResMaps) ?
      0 :
      activeMapIndex;
  }

  generateMaps(mapDefs, allowHighResMaps, useHighResMaps, activeMapIndex) {
    return (allowHighResMaps && useHighResMaps) ?
      // Only allow a single high-res map to be loaded at a time.
      // Otherwise, we run into rendering issues on low-end devices.
      // (I think the issue is that they run out of GPU memory and can't load
      // all the required tile textures, so some tiles are rendered black).
      this.generateHighResolutionMaps(mapDefs[activeMapIndex]) :
      this.generateMediumResolutionMaps(mapDefs);
  }

  generateMediumResolutionMaps(mapDefs) {
    return mapDefs.map(mapDef =>
      createSingleTileImageLayer(`single_tiles/med_res/${mapDef.name}-medres`)
    );
  }

  generateHighResolutionMaps(mapDef) {
    return [createTiledImageLayer(`${mapDef.name}-512`, {
      minimumLevel: 3,
      tileSize: 512,
    })];
  }

  getSkyboxImages() {
    return (this.props.selectionMode === SearchActions.SelectionMode.select) ?
      selectSkyboxImages :
      compareSkyboxImages;
  }

  render() {
    return (
      <div className={classNames("Globe", {"Globe--disabled": this.props.isBusy})}>
        <CesiumGlobe
          ref={element => this.globe = element}
          allLayers={this.state.allMaps}
          activeLayers={this.state.activeMaps}
          vectors={this.props.vectors}
          labels={this.props.labels}
          markers={[...this.props.pinMarkers, ...this.props.favoriteMarkers]}
          skyboxImages={this.getSkyboxImages()}
          onTap={::this.onTap}
          onSelect={::this.onSelect}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  mode: state.globe.mode,
  morphResolver: state.globe.morphResolver,
  flyToResolver: state.globe.flyToResolver,
  flyToLocation: state.globe.flyToLocation,
  flyToDuration: state.globe.flyToDuration,
  isBusy: state.busy.isBusy,
  activeLayerIndex: state.layer.activeLayerIndex,
  allowHighResMaps: state.layer.highResEnabled,
  useHighResMaps: state.layer.layers[state.layer.activeLayerIndex].useHighResMaps,
  mapDefs: state.layer.layers[state.layer.activeLayerIndex].maps,
  lowResLayers: state.layer.lowResLayers,
  vectors: state.vector.vectors,
  labels: state.vector.labels,
  selectionMode: state.search.selectionMode,
  pinMarkers: state.search.searchMarkers,
  favoriteMarkers: state.favorites.favoriteMarkers,
  activeMapIndex: state.layer.activeMapIndex,
});

const mapDispatchToProps = {
  clearSearchTerm: SearchActions.clearSearchTerm,
  executeTapQuery: SearchActions.executeTapQuery,
  selectPin: SearchActions.selectPin,
  selectFavorite: SearchActions.selectFavorite,
  clearPins: SearchActions.clearPins,
  clearSelection: SearchActions.clearSelection,
};

export { Globe };
export default connect(mapStateToProps, mapDispatchToProps)(Globe);



// WEBPACK FOOTER //
// ./src/components/Globe.js